/**
 * components/hazard/HazardTypeSection.js — Аюулын төрөл сонгох хэсэг.
 *
 * Props:
 *   categories  : [{ id, name }] — DB-ээс ачаалсан жагсаалт
 *   value       : categoryId
 *   onChange    : (categoryId) => void
 *   customName  : Optional — аюулын нэр (subhazard) гэх мэт
 *   onCustomNameChange
 *   hazards     : Optional — { id, name, category_id, code } — субтөрөл
 *   showHazardPicker : «Аюулын нэр / нэрс» сонгогчийг харуулах эсэх.
 *                      Анхдагч — ҮНЭН (5А / 15 асуумжийн урсгалд хэрэгтэй).
 *                      Аюул/зөрчил бүртгэх хуудсанд илүүдэл алхам тул
 *                      тэндээс false-оор дамжуулж нууна.
 */
import React from "react";
import {
  Card,
  SectionTitle,
  iconForCategory,
  isFaIconName,
  inputStyle,
} from "./common";

export default function HazardTypeSection({
  categories = [],
  value,
  onChange,
  hazards,
  hazardId,
  onHazardIdChange,
  customName = "",
  onCustomNameChange,
  max = 2,
  showHazardPicker = true,
}) {
  // value нь массив эсвэл ганц утга байж болно — массив болгож нормчилно.
  // Ингэснээр 1-2 аюулын төрлийг зэрэг сонгоно (max = 2).
  const selected = Array.isArray(value)
    ? value.map(String)
    : value != null && value !== ""
      ? [String(value)]
      : [];

  const toggle = (id) => {
    const key = String(id);
    let next;
    if (selected.includes(key)) {
      next = selected.filter((v) => v !== key);
    } else if (selected.length >= max) {
      // max хүрсэн бол хамгийн эртнийг гаргаж шинээр нэмнэ (дараалал хадгална)
      next = [...selected.slice(1), key];
    } else {
      next = [...selected, key];
    }
    onChange?.(next.map((v) => Number(v)));
  };

  // Тухайн категорид хамаарах субтөрөл — эхний сонгосон ангиллаар шүүнэ.
  const filteredHazards = Array.isArray(hazards)
    ? hazards.filter((h) => String(h.category_id) === selected[0])
    : [];

  // Тухайн картны padding-ийг ч багасгаж compact болгоё (Card default 16 → 8)
  return (
    <Card style={{ padding: 10 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#7A8496",
            letterSpacing: 0.4,
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          Аюулын төрөл:
        </span>
        {categories.map((c) => {
          // Эх сурвалж = ЛАВЛАХ (xab.hazard_categories.icon / color_hex).
          // Лавлахад утга байхгүй бол нэрнээс нь нөхнө — ингэснээр лавлах
          // дээр icon-оо солиход бүх хуудсанд нэг мөр өөрчлөгдөнө.
          const fb = iconForCategory(c.name);
          const ic = {
            icon: isFaIconName(c.icon) ? c.icon : fb.icon,
            color: c.color_hex || fb.color,
          };
          const active = selected.includes(String(c.id));
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => toggle(c.id)}
              title={c.name}
              aria-label={c.name}
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                border:
                  (active ? "1.5px solid " : "1px solid ") +
                  (active ? ic.color : ic.color + "40"),
                background: active ? ic.color : ic.color + "14",
                boxShadow: active
                  ? "0 1px 4px " + ic.color + "44"
                  : "none",
                cursor: "pointer",
                transition: "all 0.15s",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                flexShrink: 0,
              }}
            >
              <i
                className={"fas " + ic.icon}
                style={{
                  fontSize: 11,
                  color: active ? "#fff" : ic.color,
                }}
              />
            </button>
          );
        })}
        {/* Сонгосон ангиллын нэрийг icon-уудын хажуу талд харуулна */}
        {selected.length > 0 && (
          <span
            style={{
              marginLeft: 4,
              fontSize: 11.5,
              fontWeight: 600,
              color: "#3A4153",
              whiteSpace: "nowrap",
            }}
          >
            <i
              className="fas fa-check-circle"
              style={{ color: "#16A34A", marginRight: 4 }}
            />
            {selected
              .map(
                (v) => categories.find((c) => String(c.id) === v)?.name,
              )
              .filter(Boolean)
              .join(", ")}
          </span>
        )}
      </div>
      {categories.length === 0 && (
        <div style={{ fontSize: 12, color: "#A8AFBC" }}>
          Ангилал ачаалж байна...
        </div>
      )}

      {/* Категори дотроос тодорхой аюул — байвал.
          Аюул/зөрчил бүртгэх хуудсанд илүүдэл алхам тул тэндээс
          showHazardPicker={false}-оор нууна. */}
      {showHazardPicker && value && filteredHazards.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <SectionTitle>Аюулын нэр / нэрс</SectionTitle>
          <select
            value={hazardId || ""}
            onChange={(e) =>
              onHazardIdChange?.(e.target.value ? Number(e.target.value) : null)
            }
            style={inputStyle}
          >
            <option value="">Сонгох...</option>
            {filteredHazards.map((h) => (
              <option key={h.id} value={h.id}>
                {(h.code ? h.code + " — " : "") + h.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Чөлөөт текст хэлбэрийн нэр (зарим горимд хэрэгтэй) */}
      {onCustomNameChange && (
        <div style={{ marginTop: 12 }}>
          <SectionTitle>Аюулын тодорхой нэр</SectionTitle>
          <input
            value={customName}
            onChange={(e) => onCustomNameChange(e.target.value)}
            placeholder="Жш: Цахилгаан гүйдэлд цохиулах"
            style={inputStyle}
          />
        </div>
      )}
    </Card>
  );
}
